import { useState, useEffect } from 'react';
import { competitionScheduleService, CompetitionSchedule } from '../services/api';
import { Upload, Calendar, MapPin, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AdminCompetitionSchedulePage = () => {
    const { t } = useTranslation();
    const [schedules, setSchedules] = useState<CompetitionSchedule[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        date: '',
        location: '',
        isActive: true,
    });

    useEffect(() => {
        fetchSchedules();
    }, [page]);

    const fetchSchedules = async () => {
        try {
            setLoading(true);
            const response = await competitionScheduleService.getAdminSchedules({ page, limit: 10 });
            setSchedules(response.data.data.schedules);
            setTotalPages(response.data.data.totalPages);
        } catch (error) {
            console.error('Failed to fetch schedules:', error);
            setError('Failed to fetch schedules');
        } finally {
            setLoading(false);
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError(t('carouselManagement.form.sizeLimit'));
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitLoading(true);
        setError('');
        setSuccess('');

        try {
            const form = new FormData();
            form.append('title', formData.title);
            form.append('description', formData.description);
            form.append('date', formData.date);
            form.append('location', formData.location);
            form.append('isActive', String(formData.isActive));
            if (imageFile) {
                form.append('image', imageFile);
            }

            if (editingId) {
                await competitionScheduleService.updateSchedule(editingId, form);
                setSuccess(t('competitionSchedule.admin.successUpdate'));
            } else {
                await competitionScheduleService.createSchedule(form);
                setSuccess(t('competitionSchedule.admin.successCreate'));
            }

            resetForm();
            fetchSchedules();
            setShowForm(false);
            setTimeout(() => setSuccess(''), 3000);
        } catch (error: any) {
            console.error('Failed to save schedule:', error);
            setError(error.response?.data?.message || 'Failed to save schedule');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleEdit = (schedule: CompetitionSchedule) => {
        setEditingId(schedule.id);
        setFormData({
            title: schedule.title,
            description: schedule.description,
            date: new Date(schedule.date).toISOString().slice(0, 16), // Format for datetime-local input
            location: schedule.location,
            isActive: schedule.isActive ?? true,
        });
        setImagePreview(schedule.image || '');
        setShowForm(true);
        // Scroll to top
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id: number) => {
        if (!confirm(t('competitionSchedule.admin.confirmDelete'))) return;

        try {
            await competitionScheduleService.deleteSchedule(id);
            setSuccess(t('competitionSchedule.admin.successDelete'));
            fetchSchedules();
            setTimeout(() => setSuccess(''), 3000);
        } catch (error: any) {
            console.error('Failed to delete schedule:', error);
            setError(error.response?.data?.message || 'Failed to delete schedule');
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            description: '',
            date: '',
            location: '',
            isActive: true,
        });
        setImageFile(null);
        setImagePreview('');
        setEditingId(null);
        setError('');
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">
                        {t('competitionSchedule.admin.title')}
                    </h1>
                    <button
                        onClick={() => {
                            resetForm();
                            setShowForm(!showForm);
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        {showForm ? t('common.cancel') : t('competitionSchedule.admin.newEvent')}
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                        {success}
                    </div>
                )}

                {/* Form */}
                {showForm && (
                    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                        <h2 className="text-xl font-semibold mb-4">
                            {editingId
                                ? t('competitionSchedule.admin.editEvent')
                                : t('competitionSchedule.admin.createEvent')}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Image Upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('competitionSchedule.admin.form.image')}
                                </label>
                                <div className="flex items-start gap-4">
                                    <label className="flex flex-col items-center justify-center w-64 h-40 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-50">
                                        {imagePreview ? (
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                                className="w-full h-full object-cover rounded-lg"
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                <Upload className="w-10 h-10 text-gray-400 mb-2" />
                                                <p className="text-sm text-gray-500">
                                                    {t('carouselManagement.form.uploadHelp')}
                                                </p>
                                                <p className="text-xs text-gray-400">
                                                    {t('carouselManagement.form.sizeLimit')}
                                                </p>
                                            </div>
                                        )}
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                        />
                                    </label>
                                    {imagePreview && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setImagePreview('');
                                                setImageFile(null);
                                            }}
                                            className="text-sm text-red-600 hover:text-red-800"
                                        >
                                            {t('carouselManagement.form.removeImage')}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('competitionSchedule.admin.form.title')}
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder={t('competitionSchedule.admin.form.titlePlaceholder')}
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('competitionSchedule.admin.form.date')}
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('competitionSchedule.admin.form.location')}
                                </label>
                                <input
                                    type="text"
                                    value={formData.location}
                                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('competitionSchedule.admin.form.locationPlaceholder')}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('competitionSchedule.admin.form.description')}
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={4}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder={t('competitionSchedule.admin.form.descPlaceholder')}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('competitionSchedule.admin.form.status')}
                                </label>
                                <select
                                    value={formData.isActive ? 'active' : 'inactive'}
                                    onChange={(e) =>
                                        setFormData({ ...formData, isActive: e.target.value === 'active' })
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="active">{t('carouselManagement.form.active')}</option>
                                    <option value="inactive">{t('carouselManagement.form.inactive')}</option>
                                </select>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="submit"
                                    disabled={submitLoading}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                                >
                                    {submitLoading
                                        ? t('carouselManagement.form.saving')
                                        : editingId
                                            ? t('carouselManagement.form.update')
                                            : t('carouselManagement.form.save')}
                                </button>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                >
                                    {t('carouselManagement.form.cancel')}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* List */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t('competitionSchedule.admin.table.image')}
                                    </th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t('competitionSchedule.admin.table.title')}
                                    </th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t('competitionSchedule.admin.table.date')}
                                    </th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t('competitionSchedule.admin.table.location')}
                                    </th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t('competitionSchedule.admin.table.status')}
                                    </th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        {t('competitionSchedule.admin.table.actions')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                            {t('common.loading')}
                                        </td>
                                    </tr>
                                ) : schedules.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                            {t('competitionSchedule.noEvents')}
                                        </td>
                                    </tr>
                                ) : (
                                    schedules.map((schedule) => (
                                        <tr key={schedule.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {schedule.image ? (
                                                    <img
                                                        src={schedule.image}
                                                        alt={schedule.title}
                                                        className="h-12 w-20 object-cover rounded"
                                                    />
                                                ) : (
                                                    <div className="h-12 w-20 bg-gray-200 rounded flex items-center justify-center">
                                                        <span className="text-xs text-gray-500">No Image</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900">{schedule.title}</div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="w-4 h-4" />
                                                    {new Date(schedule.date).toLocaleDateString()}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                <div className="flex items-center gap-1">
                                                    <MapPin className="w-4 h-4" />
                                                    <span className="truncate max-w-[150px]">{schedule.location}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${schedule.isActive
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                        }`}
                                                >
                                                    {schedule.isActive
                                                        ? t('carouselManagement.form.active')
                                                        : t('carouselManagement.form.inactive')}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <div className="flex gap-3">
                                                    <button
                                                        onClick={() => handleEdit(schedule)}
                                                        className="text-blue-600 hover:text-blue-900"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(schedule.id)}
                                                        className="text-red-600 hover:text-red-900"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                        <div className="flex items-center justify-between">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() => setPage(Math.max(1, page - 1))}
                                    disabled={page === 1}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                                    disabled={page === totalPages}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Page <span className="font-medium">{page}</span> of{' '}
                                        <span className="font-medium">{totalPages}</span>
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                        <button
                                            onClick={() => setPage(Math.max(1, page - 1))}
                                            disabled={page === 1}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                                        >
                                            <span className="sr-only">Previous</span>
                                            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                        <button
                                            onClick={() => setPage(Math.min(totalPages, page + 1))}
                                            disabled={page === totalPages}
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                                        >
                                            <span className="sr-only">Next</span>
                                            <ChevronRight className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminCompetitionSchedulePage;
